import { useState, useMemo } from 'react';
import { trpc } from '../../api/trpc';
import { AuditFilters, EMPTY_FILTERS, type AuditFilterState } from './AuditFilters';
import { AuditRecordRow } from './AuditRecordRow';
import { AuditExport } from './AuditExport';
import { AuditSavedSearches } from './AuditSavedSearches';

const PAGE_SIZES = [25, 50, 100] as const;

export function AuditLog() {
  const [filters, setFilters] = useState<AuditFilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);

  // Build tRPC query input from filter state
  const queryInput = useMemo(() => ({
    ...(filters.eventType ? { eventType: filters.eventType } : {}),
    ...(filters.eventCategory ? { eventCategory: filters.eventCategory as 'conversation' | 'system_configuration' | 'task_agent' | 'security_event' | 'data_access' } : {}),
    ...(filters.actorId ? { actorId: filters.actorId } : {}),
    ...(filters.conversationId ? { conversationId: filters.conversationId } : {}),
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
    ...(filters.searchQuery ? { searchQuery: filters.searchQuery } : {}),
    limit: pageSize,
    offset: page * pageSize,
  }), [filters, page, pageSize]);

  const countInput = useMemo(() => ({
    ...(filters.eventType ? { eventType: filters.eventType } : {}),
    ...(filters.eventCategory ? { eventCategory: filters.eventCategory as 'conversation' | 'system_configuration' | 'task_agent' | 'security_event' | 'data_access' } : {}),
    ...(filters.actorId ? { actorId: filters.actorId } : {}),
    ...(filters.conversationId ? { conversationId: filters.conversationId } : {}),
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
  }), [filters]);

  const { data: records, isLoading } = trpc.audit.list.useQuery(queryInput);
  const { data: totalCount } = trpc.audit.count.useQuery(countInput);

  const total = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleFiltersChange = (newFilters: AuditFilterState) => {
    setFilters(newFilters);
    setPage(0);
    setExpandedRecordId(null);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  };

  return (
    <div className="audit-log">
      <h2 className="settings-section-title">Audit Log</h2>
      <p className="settings-section-desc">
        Immutable record of all system activity for compliance and forensics.
      </p>

      <div className="audit-toolbar">
        <AuditFilters filters={filters} onChange={handleFiltersChange} />
        <div className="audit-toolbar-actions">
          <AuditSavedSearches currentFilters={filters} onApply={handleFiltersChange} />
          <AuditExport filters={filters} />
        </div>
      </div>

      <div className="audit-table">
        <div className="audit-table-header">
          <span>#</span>
          <span>Timestamp</span>
          <span>Category</span>
          <span>Event</span>
          <span>Chat</span>
          <span>Actor</span>
          <span>Resource</span>
          <span></span>
        </div>

        {isLoading && <div className="audit-loading">Loading audit records...</div>}

        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {!isLoading && records && records.length > 0 && records.map((record: any) => (
          <AuditRecordRow
            key={record.id}
            record={record}
            isExpanded={expandedRecordId === record.id}
            onToggle={() => setExpandedRecordId(
              expandedRecordId === record.id ? null : record.id
            )}
          />
        ))}

        {!isLoading && (!records || records.length === 0) && (
          <div className="audit-empty">
            {total === 0 && !filters.searchQuery && !filters.eventCategory && !filters.actorId && !filters.conversationId ? (
              <>
                No audit activity yet.
                <div className="audit-empty-hint">
                  Audit records are created automatically as you use Cade.
                </div>
              </>
            ) : (
              <>
                No audit records match the current filters.
                <div className="audit-empty-hint">
                  Try adjusting your search or filter criteria.
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="audit-pagination">
          <span className="audit-pagination-info">
            Showing {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="audit-pagination-controls">
            <select
              className="audit-page-size-select"
              value={pageSize}
              onChange={e => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZES.map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              Previous
            </button>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
